import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import logo from "@/assets/landing/logo-rocortado.png";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase.ts";

export const Route = createFileRoute("/auth/forgot-password")({
	beforeLoad: () => {
		if (pb.authStore.isValid) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: RouteComponent,
});

const formSchema = z.object({
	email: z.email(),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
	const router = useRouter();
	const { t } = useTranslation();

	const forgotPassword = useMutation({
		mutationFn: (data: FormValues) => {
			toast.loading(
				t(($) => $.modules.auth.forgotPassword.status.loading.title),
				{
					id: "forgot-password",
					description: t(($) => $.modules.auth.forgotPassword.status.loading.message),
				}
			);

			return pb.collection(Collections.Doctors).requestPasswordReset(data.email);
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.auth.forgotPassword.status.success.title),
				{
					id: "forgot-password",
					description: t(($) => $.modules.auth.forgotPassword.status.success.message),
				}
			);

			router.navigate({ to: "/auth/login" });
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.forgotPassword.status.error.title),
					{
						id: "forgot-password",
						description: parsed.message || t(($) => $.modules.auth.forgotPassword.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("forgot-password");
		},
	});

	const defaultValues: FormValues = {
		email: "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },
		onSubmit: ({ value }) => {
			forgotPassword.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	return (
		<div className="mx-auto w-full max-w-md space-y-8">
			<div className="flex flex-col items-center">
				<img alt="Logo" height={"192.667"} src={logo} width={"447.967"} />
				<h2 className="mt-6 text-center font-bold text-3xl text-foreground tracking-tight">
					{t(($) => $.modules.auth.forgotPassword.title)}
				</h2>
			</div>

			<form.AppForm>
				<form className="space-y-6" onSubmit={handleSubmit}>
					<form.AppField name="email">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.email.label)}</field.FieldLabel>
								<field.FieldControl>
									<Input
										autoComplete="email"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.common.fields.email.placeholder)}
										type="email"
										value={field.state.value}
									/>
								</field.FieldControl>

								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<Button className="w-full" disabled={forgotPassword.isPending} type="submit">
						{t(($) => $.modules.auth.forgotPassword.buttons.submit)}
					</Button>
				</form>
			</form.AppForm>
			<div className="mt-4 text-center">
				<span className="text-muted-foreground text-sm">
					{t(($) => $.modules.auth.forgotPassword.buttons.register.question)}
				</span>{" "}
				<Link className="font-medium text-primary text-sm hover:underline" to="/auth/register">
					{t(($) => $.modules.auth.forgotPassword.buttons.register.answer)}
				</Link>
			</div>
		</div>
	);
}
